import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, CardBody, Spinner } from "reactstrap";
import moment from "moment";

import { LogGroupBy, LogGroupDto } from "../../interfaces";
import { LogLevelBadge } from "./LogLevelBadge";

const GROUP_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

type LogGroupsPanelProps = {
  groups?: LogGroupDto[];
  isLoading?: boolean;
  title?: string;
  subtitle?: string;
  groupBy: LogGroupBy;
  onChangeGroupBy: (groupBy: LogGroupBy) => void;
  onSelectGroup: (group: LogGroupDto) => void;
};

const GROUP_BY_OPTIONS: LogGroupBy[] = ["signature", "message", "ip"];

function groupByLabelKey(groupBy: LogGroupBy): string {
  if (groupBy === "message") return "groupByMessage";
  if (groupBy === "ip") return "groupByIp";
  return "groupBySignature";
}

function groupHintKey(groupBy: LogGroupBy): string {
  if (groupBy === "message") return "groupedByMessageHint";
  if (groupBy === "ip") return "groupedByIpHint";
  return "groupedIssuesHint";
}

export const LogGroupsPanel: React.FC<LogGroupsPanelProps> = ({
  groups,
  isLoading,
  title,
  subtitle,
  groupBy,
  onChangeGroupBy,
  onSelectGroup,
}) => {
  const { t } = useTranslation();

  return (
    <Card className="logs-groups-panel">
      <CardBody>
        <div className="logs-groups-panel__header">
          <div>
            <h6 className="mb-1">{title || t("groupedIssues")}</h6>
            <p className="text-muted mb-0 small">
              {subtitle || t(groupHintKey(groupBy))}
            </p>
          </div>
          <div className="logs-group-by">
            <span className="text-muted small">{t("groupBy")}</span>
            <div className="btn-group btn-group-sm" role="group">
              {GROUP_BY_OPTIONS.map((option) => (
                <Button
                  key={option}
                  color={groupBy === option ? "primary" : "light"}
                  size="sm"
                  onClick={() => onChangeGroupBy(option)}
                >
                  {t(groupByLabelKey(option))}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : !groups?.length ? (
          <div className="text-center text-muted py-5">
            {t("noRecordsFound")}
          </div>
        ) : (
          <div className="logs-groups-list">
            {groups.map((group, index) => {
              const extraMessages = Math.max(
                (group.uniqueMessages ?? 0) - 1,
                0,
              );
              return (
                <button
                  type="button"
                  key={`${groupBy}-${group.serviceName}-${group.count}-${index}`}
                  className={`logs-group-item logs-group-item--${group.level}`}
                  onClick={() => onSelectGroup(group)}
                >
                  <span className="logs-group-item__count">{group.count}</span>
                  <span className="logs-group-item__body">
                    <span className="logs-group-item__title">
                      {groupBy === "ip" ? (
                        <span className="logs-group-item__ip">
                          {group.ip || t("unknownIp")}
                        </span>
                      ) : (
                        <LogLevelBadge level={group.level} />
                      )}
                      <span className="logs-group-item__service">
                        {group.serviceName}
                      </span>
                      {groupBy === "ip" && (
                        <LogLevelBadge level={group.level} />
                      )}
                      {group.errorName && groupBy !== "ip" && (
                        <span className="logs-group-item__error">
                          {group.errorName}
                        </span>
                      )}
                      {group.statusCode ? (
                        <span className="logs-group-item__status">
                          {group.statusCode}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={`logs-group-item__message${
                        groupBy === "message"
                          ? " logs-group-item__message--clamp"
                          : ""
                      }`}
                      title={group.sampleMessage}
                    >
                      {group.sampleMessage || "-"}
                      {groupBy === "ip" && extraMessages > 0
                        ? ` (+${extraMessages})`
                        : ""}
                    </span>
                    {(group.sampleMethod || group.samplePath) && (
                      <span
                        className="logs-group-item__path"
                        title={group.samplePath || ""}
                      >
                        {[group.sampleMethod, group.samplePath]
                          .filter(Boolean)
                          .join(" ")}
                      </span>
                    )}
                    <span className="logs-group-item__meta text-muted">
                      <span>
                        {t("dateTimeFrom")}:{" "}
                        {moment(group.firstSeen).format(GROUP_DATE_TIME_FORMAT)}
                      </span>
                      <span>
                        {t("dateTimeTo")}:{" "}
                        {moment(group.lastSeen).format(GROUP_DATE_TIME_FORMAT)} (
                        {moment(group.lastSeen).fromNow()})
                      </span>
                      {groupBy !== "ip" && group.ip && (
                        <span>
                          {t("ip")}: {group.ip}
                        </span>
                      )}
                      {groupBy !== "ip" && (group.uniqueIps ?? 0) > 1 && (
                        <span>
                          {t("uniqueIps")}: {group.uniqueIps}
                        </span>
                      )}
                      {groupBy === "ip" && (
                        <span>
                          {t("uniqueMessages")}: {group.uniqueMessages ?? 0}
                        </span>
                      )}
                      {(group.uniqueServices ?? 0) > 1 && (
                        <span>
                          {t("services")}: {group.uniqueServices}
                        </span>
                      )}
                      {group.affectedUsers > 0 && (
                        <span>
                          {t("affectedUsers")}: {group.affectedUsers}
                        </span>
                      )}
                    </span>
                  </span>
                  <i className="ri-arrow-right-s-line logs-group-item__chevron" />
                </button>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
};
